import { BigNumber } from "@yjsgames/bignumber";

/**
 * ProjectileSpec — 발사체의 "최종 설계도" 데이터 (지시문 §9).
 * 모듈은 발사체를 직접 수정하지 않고 이 spec만 변형한다 (apply). ProjectileFactory가 spec으로 실제 발사체를 만든다.
 *
 * 런타임 효과는 categorical enum(element/behavior/impact)이 아니라 **수치 필드**가 구동한다 —
 * 그래서 모듈 효과가 누적·중첩으로 즉시 화면에 드러난다 (성공 기준 §30):
 *  - chainCount > 0  → 연쇄 피해 (Electric 모듈만으로도 발생)
 *  - explosionRadius > 0 → 범위 피해
 *  - pierceCount > 0 → 관통
 *  - burstCount > 1 → 한 발사에 패턴을 시간차로 반복 (연사)
 *  - critChance > 0 → 발사체별 확률 치명타 (피해 배율)
 *  - goldMultiplier > 1 → 처치 골드 가산 (탐욕, 피해와 무관)
 *  - element/behavior → 색상·이동 패턴(시각 차별화)
 */
export type ProjectileElement = "none" | "fire" | "electric" | "ice";
export type ProjectileBehavior = "straight" | "bounce" | "homing";
export type ProjectileImpact = "normal" | "explosion" | "pierce" | "chain";

export interface ProjectileSpec {
  count: number;
  /** 기본 피해 — 무한 성장하므로 BigNumber. 모듈은 이 값을 직접 건드리지 않고 damageMultiplier만 조정. */
  baseDamage: BigNumber;
  damageMultiplier: number;
  speedMultiplier: number;
  element: ProjectileElement;
  behavior: ProjectileBehavior;
  impact: ProjectileImpact;
  pierceCount: number;
  chainCount: number;
  explosionRadius: number;
  splitCount: number;
  /** 연사(burst) — 한 번 발사에 이 패턴을 burstCount회 시간차로 발사 (기본 1). GameScene.fire가 delayedCall로 반복. */
  burstCount: number;
  /** 연사 2번째 이후 발사 간 지연(ms). burstCount<=1이면 무의미. */
  burstDelayMs: number;
  /** 크리티컬 발동 확률(0~1, 기본 0). 발사체별로 ProjectileFactory.spawn이 rollCrit로 판정. */
  critChance: number;
  /** 크리티컬 발동 시 damageMultiplier에 곱할 배수 (기본 1 = 효과 없음). */
  critMultiplier: number;
  /** 탐욕(greed) — 처치 골드에만 곱하는 배수 (기본 1, 피해 무관). GameScene.damageBrick 골드 계산에서 적용. */
  goldMultiplier: number;
  /**
   * 벽 반사 횟수 — 반사(bounce) 모듈 수만큼 누적(기본 0). Projectile이 bounceLeft로 차감하며,
   * 남아 있으면 프레임 벽에서 튕기고(1 차감), 0이면 좌/우/상 벽에 닿는 순간 소멸한다.
   * pierceCount/burstCount와 동일한 "수치 필드가 효과를 구동" 패턴.
   */
  bounceCount: number;
  /**
   * 처형(execute) — 충돌한 벽돌의 체력이 maxHp의 executeThreshold(0~1) 비율 **이하**일 때
   * executeChance 확률로 즉사시킨다. 둘 다 기본 0(미보유). 충돌 시점 판정이라 BattlefieldRunner가 rollExecute로 소비.
   */
  executeThreshold: number;
  executeChance: number;
  /** 충격파(shockwave) — 충돌 지점 주변 벽돌에 약한 파동 피해를 준다. radius<=0이면 미보유. */
  shockwaveRadius: number;
  /** 충격파 피해 비율 — 직격 피해에 곱해 주변 피해량을 만든다. */
  shockwaveDamageRatio: number;
  /** 여진(aftershock) — 충돌 후 같은 벽돌에 지연 2차 피해를 준다. ratio<=0이면 미보유. */
  aftershockDamageRatio: number;
  /** 여진 지연 시간(ms). */
  aftershockDelayMs: number;
  /** 표식(mark) — 충돌한 벽돌에 약점 상태를 부여할 확률. 0이면 미보유. */
  markChance: number;
  /** 표식 대상이 받는 피해 배수. markHits가 남아 있는 동안 모든 타격에 적용된다. */
  markDamageMultiplier: number;
  /** 표식 유지 타격 수. 피해를 실제로 받은 타격마다 1씩 소모된다. */
  markHits: number;
  /** 모듈 기반 발사 간격 배수 (기본 1). 1 미만이면 쿨타임 감소(속사), CombatSystem.fireIntervalMs가 곱으로 소비. */
  fireIntervalMultiplier: number;
  /**
   * 이 발사체에 기여한 모듈 definitionId 목록 (중복 제거, 융합 컴포넌트 포함). CombatSystem.buildSpec이 채운다.
   * 숙련도 카운터(activations/hits/kills) 귀속용 — BattlefieldRunner가 발사/타격/처치 시 각 id의 카운터를 누적.
   * 분열 자탄도 부모 목록을 계승(deriveSplitSpec). 기본 빈 배열(모듈 미장착 기본 발사체).
   */
  sourceModuleIds: readonly string[];
}

/** 모듈 미장착 시의 기본 발사체 스펙. baseDamage는 환생 영구 보너스 반영용 (기본 1). */
export function createDefaultProjectileSpec(baseDamage: BigNumber = BigNumber.ONE): ProjectileSpec {
  return {
    count: 1,
    baseDamage,
    damageMultiplier: 1,
    speedMultiplier: 1,
    element: "none",
    behavior: "straight",
    impact: "normal",
    pierceCount: 0,
    chainCount: 0,
    explosionRadius: 0,
    splitCount: 0,
    burstCount: 1,
    burstDelayMs: 0,
    critChance: 0,
    critMultiplier: 1,
    goldMultiplier: 1,
    bounceCount: 0,
    executeThreshold: 0,
    executeChance: 0,
    shockwaveRadius: 0,
    shockwaveDamageRatio: 0,
    aftershockDamageRatio: 0,
    aftershockDelayMs: 0,
    markChance: 0,
    markDamageMultiplier: 1,
    markHits: 0,
    fireIntervalMultiplier: 1,
    sourceModuleIds: [],
  };
}

/** spec → 최종 1발당 피해량. damageMultiplier(배율)에 element/레벨/크리티컬 보정이 이미 반영돼 있다. 최소 1. */
export function computeProjectileDamage(spec: ProjectileSpec): BigNumber {
  return spec.baseDamage.mul(BigNumber.fromNumber(spec.damageMultiplier)).max(BigNumber.ONE);
}

/**
 * 크리티컬 발동 시 모듈별 고유 강화 계수 — 발사체 단위로 강화 가능한 효과만 (값은 global.tsv SSoT, balance에서 주입).
 * count/burst(발사 수 단위)는 발사체별 rollCrit 시점에 이미 spawn 루프가 돌아 바꿔도 무의미하므로 제외.
 * explosionColor(링 색)는 spec 변형이 아니라 충돌 시 런타임 표시값이라 여기 미포함 — GameScene이 직접 사용.
 */
export interface CritMods {
  /** 폭발 반경 배수 (explosionRadius > 0일 때만 곱). */
  explosionRadiusMul: number;
  /** 관통 횟수 가산 (pierceCount > 0일 때만). */
  pierceBonus: number;
  /** 연쇄 타겟 가산 (chainCount > 0일 때만). */
  chainBonus: number;
  /** 분열 추가탄 가산 (splitCount > 0일 때만). */
  splitBonus: number;
  /** 탐욕 골드 배수 (goldMultiplier > 1일 때만 곱). */
  greedMul: number;
}

/**
 * 발사체별 크리티컬 판정 — critChance 확률로 성공 시 damageMultiplier에 critMultiplier를 곱한 새 spec을 반환.
 * 순수함수(rng 주입)라 테스트는 결정론적. 실제 호출은 ProjectileFactory.spawn에서 발사체마다 rng=Math.random.
 * critChance<=0이면 항상 비크리티컬 (입력 spec 그대로 반환 — 불필요한 복제 방지).
 *
 * mods를 주면 크리티컬 시 모듈별 고유 강화를 추가 적용한다 — 해당 모듈 필드가 활성(반경/관통/연쇄/분열 >0, 골드배수 >1)일 때만.
 * mods 미주입이면 기존 동작(damageMultiplier만)을 유지한다.
 */
export function rollCrit(
  spec: ProjectileSpec,
  rng: () => number,
  mods?: CritMods,
): { spec: ProjectileSpec; isCrit: boolean } {
  if (spec.critChance <= 0 || rng() >= spec.critChance) return { spec, isCrit: false };
  const crit: ProjectileSpec = {
    ...spec,
    damageMultiplier: spec.damageMultiplier * spec.critMultiplier,
  };
  if (mods) {
    if (crit.explosionRadius > 0) crit.explosionRadius *= mods.explosionRadiusMul;
    if (crit.pierceCount > 0) crit.pierceCount += mods.pierceBonus;
    if (crit.chainCount > 0) crit.chainCount += mods.chainBonus;
    if (crit.splitCount > 0) crit.splitCount += mods.splitBonus;
    if (crit.goldMultiplier > 1) crit.goldMultiplier *= mods.greedMul;
  }
  return { spec: crit, isCrit: true };
}

/**
 * 처형(execute) 판정 — 벽돌 체력이 maxHp의 executeThreshold 비율 **이하**일 때 executeChance 확률로 즉사(true).
 * 순수함수(rng 주입)라 테스트는 결정론적. 실제 호출은 BattlefieldRunner.damageBrick에서 직격 발사체에 대해 rng=Math.random.
 * threshold/chance가 0이거나 maxHp가 0이면 false(처형 미보유·div-by-zero 방어). 체력비율 == threshold면 처형 대상(이하 포함).
 */
export function rollExecute(
  hp: BigNumber,
  maxHp: BigNumber,
  executeThreshold: number,
  executeChance: number,
  rng: () => number,
): boolean {
  if (executeChance <= 0 || executeThreshold <= 0 || maxHp.lte(BigNumber.ZERO)) return false;
  const ratio = hp.div(maxHp).toNumber();
  if (ratio > executeThreshold) return false;
  return rng() < executeChance;
}
